import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dropdownCategories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when screen resizes to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) setMobileMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const linkClass = (isScrolled: boolean) =>
    `text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
      isScrolled ? 'text-foreground-600 hover:text-foreground-950' : 'text-white/80 hover:text-white'
    }`;

  const ctaClass = (isScrolled: boolean) =>
    `hidden md:inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 ${
      isScrolled
        ? 'bg-primary-500 text-white hover:bg-primary-600'
        : 'bg-white text-foreground-900 hover:bg-white/90'
    }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Left: Logo */}
          <div className="flex items-center gap-6">
            <Link to="/" className={`text-lg md:text-xl font-heading font-bold whitespace-nowrap transition-colors duration-200 ${
              scrolled ? 'text-foreground-950' : 'text-white'
            }`}>
              CSSBuy Spreadsheet
            </Link>
          </div>

          {/* Center: Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-5">
            <Link to="/" className={linkClass(scrolled)}>
              Home
            </Link>

            {/* Categories Dropdown */}
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors duration-200 ${linkClass(scrolled)}`}
              >
                <span>Categories</span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 bg-background-50 rounded-lg border border-background-200/70 shadow-lg py-2 max-h-80 overflow-y-auto z-50">
                  {dropdownCategories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150 whitespace-nowrap"
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                        <i className={`${cat.icon} text-sm`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link to="/cssbuy-spreadsheet-shopping-guide/" className={linkClass(scrolled)}>
              Shopping Guide
            </Link>
            <Link to="/cssbuy-spreadsheet-complete-guide/" className={linkClass(scrolled)}>
              Guides
            </Link>
            <Link to="/cssbuy-spreadsheet-template-guide/" className={linkClass(scrolled)}>
              Templates
            </Link>
          </div>

          {/* Right: CTA + Mobile Hamburger */}
          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={ctaClass(scrolled)}
            >
              Buy It Now
            </a>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${
                scrolled ? 'text-foreground-800 hover:bg-background-200' : 'text-white hover:bg-white/10'
              }`}
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Panel */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 shadow-lg">
          <div className="px-6 py-4 flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-800 hover:bg-background-100 transition-colors text-sm font-medium"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-home-line text-base"></i>
              </span>
              Home
            </Link>

            {/* Categories Section in Mobile */}
            <div className="px-3 py-2">
              <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-2">
                Product Categories
              </p>
              <div className="flex flex-col gap-0.5">
                {dropdownCategories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors text-sm"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                      <i className={`${cat.icon} text-sm`}></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>

            <div className="border-t border-background-200 my-2"></div>

            <Link
              to="/cssbuy-spreadsheet-shopping-guide/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-800 hover:bg-background-100 transition-colors text-sm font-medium"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-shopping-cart-line text-base"></i>
              </span>
              Shopping Guide
            </Link>

            <Link
              to="/cssbuy-spreadsheet-complete-guide/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-800 hover:bg-background-100 transition-colors text-sm font-medium"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-book-open-line text-base"></i>
              </span>
              Guides
            </Link>

            <Link
              to="/cssbuy-spreadsheet-template-guide/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-800 hover:bg-background-100 transition-colors text-sm font-medium"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-file-list-3-line text-base"></i>
              </span>
              Templates
            </Link>

            <div className="border-t border-background-200 my-2"></div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all mt-2"
            >
              Buy It Now
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}